import { For, Show } from "solid-js";
import type { ScheduleVoyage, Voyage, VoyageDetails } from "../../shared/api/types";
import { formatNumber } from "../../shared/locale";
import { fmtLocal } from "./details-format";

// Single source of truth: drives off the loaded VoyageDetails so the
// "Voyage details" column is identical regardless of which tab made the
// selection. While currentDetails is still loading we fall back to the
// click-source voyage object (placeholder) so the panel doesn't show
// '—' for everything during the fetch.
//
// stopContext is the ONLY tab-specific addendum: when the user selected
// a voyage from the Port Calendar they picked a specific stop, and the
// arrival / departure for that one stop is useful context the unified
// VoyageDetails doesn't carry. Vessels-tab selections leave it null.

export interface StopContext {
  portName: string;
  arrivalLocal: string;
  departureLocal: string;
}

export interface BasicSectionProps {
  details: VoyageDetails | null;
  placeholder: ScheduleVoyage | Voyage;
  portRefs: Record<string, string>;
  stopContext: StopContext | null;
}

export function BasicSection(props: BasicSectionProps) {
  const fields = () => {
    const d = props.details;
    const p = props.placeholder as Partial<ScheduleVoyage & Voyage>;
    const out: { k: string; v: string; muted?: string }[] = [];

    const vesselName = d?.vesselName ?? p.vesselName ?? "—";
    const vesselCode = d?.vesselCode ?? p.vesselCode;
    out.push({
      k: "Vessel",
      v: vesselName,
      ...(vesselCode ? { muted: `(${vesselCode})` } : {}),
    });

    const voyageRef = d?.voyageReference ?? p.voyageReference ?? "—";
    const voyageNo = d?.voyageNo ?? p.voyageNo;
    out.push({
      k: "Voyage",
      v: voyageRef,
      ...(voyageNo != null ? { muted: `#${voyageNo}` } : {}),
    });

    const oprType = d?.oprType ?? p.oprType;
    if (oprType) out.push({ k: "Operation", v: oprType });

    const status = d?.voyageStatus ?? p.voyageStatus;
    if (status) out.push({ k: "Status", v: status });

    const route = d?.tradeArea ?? p.tradeArea;
    if (route) out.push({ k: "Route", v: route });
    const trade = d?.lob ?? p.lob;
    if (trade) out.push({ k: "Trade", v: trade });

    // Commence / complete come from VoyageDetails; vessels placeholder
    // names them commenceDate / completeDate (no Local suffix).
    const commence = d?.commenceDateLocal ?? p.commenceDate;
    if (commence) out.push({ k: "Commence", v: fmtLocal(commence) });
    const complete = d?.completeDateLocal ?? p.completeDate;
    if (complete) out.push({ k: "Complete", v: fmtLocal(complete) });

    const seaDays = d?.totalSeaDays ?? p.totalSeaDays;
    if (seaDays != null) out.push({ k: "Sea days", v: seaDays.toFixed(1) });
    const portDays = d?.totalPortDays ?? p.totalPortDays;
    if (portDays != null) out.push({ k: "Port days", v: portDays.toFixed(1) });

    if (d?.endOfVoyageDistance != null) {
      out.push({ k: "Distance (nm)", v: formatNumber(Math.round(d.endOfVoyageDistance)) });
    }
    if (d?.opsCoordinator) out.push({ k: "Ops coord.", v: d.opsCoordinator });

    // Calendar-only addendum: which specific stop the user clicked into.
    if (props.stopContext) {
      const ref = props.portRefs[props.stopContext.portName] ?? props.stopContext.portName;
      out.push({ k: "Selected port", v: ref, muted: props.stopContext.portName });
      out.push({ k: "Arrival", v: fmtLocal(props.stopContext.arrivalLocal) });
      out.push({ k: "Departure", v: fmtLocal(props.stopContext.departureLocal) });
    }
    return out;
  };

  return (
    <dl class="details-list">
      <For each={fields()}>
        {(f) => (
          <>
            <dt>{f.k}</dt>
            <dd>
              {f.v}
              <Show when={f.muted}>
                {" "}
                <span class="muted">{f.muted}</span>
              </Show>
            </dd>
          </>
        )}
      </For>
    </dl>
  );
}
